import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Avatar from '@/components/Avatar';
import LoadingState from '@/components/LoadingState';

import { CommsBackendBadge } from '../components/CommsBackendBadge';
import { useAdminConversations } from '../api/useAdminConversations';
import { useAdminReadState } from '../api/useAdminReadState';
import { useFindOrCreateConversation } from '../api/useFindOrCreateConversation';
import { useWorkerSearch } from '../api/useWorkerSearch';
import { useWorkersByIds, type WorkerChip } from '../api/useWorkersByIds';
import { compactDateLabel } from '../lib/compactDate';
import { isUnread } from '../lib/unread';
import type { Message } from '../types';

interface CommsChatListProps {
  selectedConversationId: string | null;
  onSelectConversation: (conversationId: string) => void;
}

const sidebarStyle: React.CSSProperties = {
  width: 360,
  borderRight: '1px solid #e5e7eb',
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};

const headerStyle: React.CSSProperties = {
  padding: 12,
  borderBottom: '1px solid #e5e7eb',
};

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '8px 10px',
  fontSize: 14,
  outline: 'none',
};

const rowBaseStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  padding: '10px 12px',
  cursor: 'pointer',
  alignItems: 'flex-start',
  borderBottom: '1px solid #f3f4f6',
};

const unreadDotStyle: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: 4,
  background: '#2563eb',
  marginTop: 6,
  flexShrink: 0,
};

const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: '#6b7280',
  padding: '10px 12px 4px',
};

export function CommsChatList({
  selectedConversationId,
  onSelectConversation,
}: CommsChatListProps) {
  const {
    conversations,
    loading: convsLoading,
    loadingMore,
    hasMore,
    loadMore,
  } = useAdminConversations();
  const { readMap, markRead } = useAdminReadState();
  const [searchQuery, setSearchQuery] = useState('');
  const { results: searchResults, loading: searchLoading } =
    useWorkerSearch(searchQuery);
  const { open: openConversation, loading: opening } =
    useFindOrCreateConversation();

  const conversationWorkerIds = useMemo(
    () =>
      conversations
        .map((c) => c.conversation.worker_id)
        .filter((id): id is number => typeof id === 'number'),
    [conversations],
  );
  const { workers } = useWorkersByIds(conversationWorkerIds);

  const handleSelect = useCallback(
    (conversationId: string) => {
      onSelectConversation(conversationId);
      void markRead(conversationId);
    },
    [onSelectConversation, markRead],
  );

  const handleOpenWorker = useCallback(
    async (worker: WorkerChip) => {
      try {
        const conversationId = await openConversation(worker.id);
        setSearchQuery('');
        handleSelect(conversationId);
      } catch {
        /* error surfaced via hook state */
      }
    },
    [openConversation, handleSelect],
  );

  const isSearching = searchQuery.trim().length >= 2;

  return (
    <div style={sidebarStyle}>
      <div style={headerStyle}>
        <input
          type="search"
          placeholder="Search workers to message…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={searchInputStyle}
        />
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {isSearching ? (
          <SearchResultsList
            results={searchResults}
            loading={searchLoading || opening}
            onPick={handleOpenWorker}
          />
        ) : (
          <ConversationsList
            conversations={conversations}
            workers={workers}
            readMap={readMap}
            loading={convsLoading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            onLoadMore={loadMore}
            selectedConversationId={selectedConversationId}
            onSelect={handleSelect}
          />
        )}
      </div>
    </div>
  );
}

interface ConversationsListProps {
  conversations: ReturnType<typeof useAdminConversations>['conversations'];
  workers: Record<number, WorkerChip>;
  readMap: Record<string, string>;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => Promise<void>;
  selectedConversationId: string | null;
  onSelect: (conversationId: string) => void;
}

function ConversationsList({
  conversations,
  workers,
  readMap,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  selectedConversationId,
  onSelect,
}: ConversationsListProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const target = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          void onLoadMore();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(target);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, onLoadMore]);

  if (loading && conversations.length === 0) {
    return <LoadingState />;
  }
  if (conversations.length === 0) {
    return (
      <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>
        No conversations yet. Use the search above to message a worker.
      </div>
    );
  }
  return (
    <>
      <div style={sectionLabel}>Chats</div>
      {conversations.map((item) => (
        <ConversationRow
          key={item.conversation.id}
          conversation={item.conversation}
          lastMessage={item.last_message}
          worker={
            item.conversation.worker_id != null
              ? workers[item.conversation.worker_id]
              : undefined
          }
          isSelected={item.conversation.id === selectedConversationId}
          isUnread={isUnread(
            item.last_message.created_at,
            readMap[item.conversation.id],
          )}
          onSelect={onSelect}
        />
      ))}
      {hasMore ? (
        <div
          ref={sentinelRef}
          style={{
            padding: 12,
            textAlign: 'center',
            fontSize: 12,
            color: '#6b7280',
          }}
        >
          {loadingMore ? 'Loading more…' : ' '}
        </div>
      ) : null}
    </>
  );
}

interface ConversationRowProps {
  conversation: ReturnType<
    typeof useAdminConversations
  >['conversations'][number]['conversation'];
  lastMessage: Message;
  worker: WorkerChip | undefined;
  isSelected: boolean;
  isUnread: boolean;
  onSelect: (conversationId: string) => void;
}

function ConversationRow({
  conversation,
  lastMessage,
  worker,
  isSelected,
  isUnread,
  onSelect,
}: ConversationRowProps) {
  const name = workerName(worker, conversation.title);
  const dateLabel = lastMessageLabel(lastMessage.created_at);
  return (
    <div
      onClick={() => onSelect(conversation.id)}
      style={{
        ...rowBaseStyle,
        background: isSelected ? '#eff6ff' : 'transparent',
      }}
    >
      <Avatar
        firstName={name}
        src={worker?.avatar_url ?? undefined}
        size="sm"
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              minWidth: 0,
            }}
          >
            <span
              title={name}
              data-testid="chat-row-name"
              style={{
                fontWeight: isUnread ? 700 : 500,
                fontSize: 14,
                color: '#111827',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {name}
            </span>
            {/* On /chat-v2 the load-bearing signal is a worker who is NOT on
                the cohort (their app won't see replies sent here) — so only
                the PubNub badge renders; Supabase is the expected state. */}
            <CommsBackendBadge backend={worker?.comms_backend} hideSupabase />
          </span>
          <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>
            {dateLabel}
          </span>
        </div>
        <div
          style={{
            fontSize: 12,
            color: isUnread ? '#374151' : '#6b7280',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginTop: 2,
          }}
        >
          {lastMessagePreview(lastMessage)}
        </div>
      </div>
      {isUnread ? <span style={unreadDotStyle} aria-label="Unread" /> : null}
    </div>
  );
}

interface SearchResultsListProps {
  results: WorkerChip[];
  loading: boolean;
  onPick: (worker: WorkerChip) => void;
}

function SearchResultsList({
  results,
  loading,
  onPick,
}: SearchResultsListProps) {
  if (loading) return <LoadingState />;
  if (results.length === 0) {
    return (
      <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>
        No matching workers.
      </div>
    );
  }
  return (
    <>
      <div style={sectionLabel}>Open a new chat</div>
      {results.map((worker) => (
        <div
          key={worker.id}
          onClick={() => onPick(worker)}
          style={rowBaseStyle}
        >
          <Avatar
            firstName={workerName(worker, null)}
            src={worker.avatar_url ?? undefined}
            size="sm"
          />
          <div style={{ flex: 1 }}>
            <div
              style={{ fontSize: 14, fontWeight: 500 }}
              title={workerName(worker, null)}
            >
              {workerName(worker, null)}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {worker.user?.email ?? ''}
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

function workerName(
  worker: WorkerChip | undefined,
  fallbackTitle: string | null,
): string {
  if (worker?.user) {
    const first = worker.user.first_name ?? '';
    const last = worker.user.last_name ?? '';
    const composed = `${first} ${last}`.trim();
    if (composed.length > 0) return composed;
    return worker.user.email ?? fallbackTitle ?? 'Worker';
  }
  return fallbackTitle ?? 'Worker';
}

function lastMessagePreview(message: Message): string {
  if (message.message_type === 'image') return '📷 Photo';
  if (message.message_type === 'system')
    return message.content ?? 'System update';
  if (message.message_type === 'broadcast')
    return `📣 ${message.content ?? ''}`;
  return message.content ?? '';
}

function lastMessageLabel(iso: string): string {
  return compactDateLabel(iso);
}
